import React, { useState, useMemo } from 'react';
import { UserX, Eye, EyeOff, X } from 'lucide-react';
import { useApp } from '../../context/AppContext';
import DataTable from '../../components/common/DataTable';
import DateInput from '../../components/common/DateInput';

const inputCls = 'w-full px-3 py-1.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-purple-300';

function DropoutModal({ student, onClose, onSave, academicYear }) {
  const today = new Date().toISOString().split('T')[0];
  const [form, setForm] = useState({ dropoutDate: today, reason: '', academicYear });
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const handleSubmit = (e) => {
    e.preventDefault();
    onSave(form);
  };

  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-md">
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100">
          <h2 className="font-semibold text-base">Mark as Drop Out</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600"><X size={18} /></button>
        </div>
        <form onSubmit={handleSubmit} className="p-5 space-y-4">
          <div>
            <p className="text-sm text-gray-500 mb-3">
              Student: <strong>{student.firstName} {student.lastName}</strong> ({student.admissionNumber})
            </p>
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">Drop Out Date</label>
            <DateInput required value={form.dropoutDate} onChange={e => set('dropoutDate', e.target.value)} className={inputCls} />
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">Reason</label>
            <textarea required value={form.reason} onChange={e => set('reason', e.target.value)}
              rows={3} placeholder="Reason for dropping out..." className={inputCls + ' resize-none'} />
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">Academic Year</label>
            <input required value={form.academicYear} onChange={e => set('academicYear', e.target.value)} className={inputCls} placeholder="e.g. 2025-2026" />
          </div>
          <div className="flex justify-end gap-3 pt-1">
            <button type="button" onClick={onClose} className="px-4 py-1.5 border border-gray-300 rounded-lg text-sm hover:bg-gray-50">Cancel</button>
            <button type="submit" className="flex items-center gap-2 px-4 py-1.5 bg-red-600 text-white rounded-lg text-sm hover:bg-red-700">
              <UserX size={15} /> Confirm Drop Out
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function Dropout() {
  const { students, classes, dropouts, saveDropout, deleteDropout, updateStudent, academicYear } = useApp();
  const [tab, setTab] = useState('active');
  const [modalStudent, setModalStudent] = useState(null);

  const dropoutStudentIds = new Set(dropouts.map(d => d.studentId));

  const activeStudents = useMemo(() =>
    students.filter(s => s.status === 'active' && !dropoutStudentIds.has(s.id)),
    [students, dropouts]
  );

  const getClassName = (classId) => {
    const cls = classes.find(c => c.id === classId);
    return cls ? `${cls.name} - ${cls.section}` : '-';
  };

  const handleMarkDropout = async (formData) => {
    const { v4: uuidv4 } = await import('uuid').catch(() => null) || {};
    const id = 'DO' + Date.now();
    const ok = await saveDropout({ id, studentId: modalStudent.id, ...formData });
    if (ok) {
      await updateStudent(modalStudent.id, { ...modalStudent, status: 'inactive' });
      setModalStudent(null);
    }
  };

  const handleRestore = async (dropout) => {
    const student = students.find(s => s.id === dropout.studentId);
    if (!student) return;
    const ok = await deleteDropout(dropout.id);
    if (ok) {
      await updateStudent(student.id, { ...student, status: 'active' });
    }
  };

  const activeColumns = [
    {
      label: 'Admission No', accessor: 'admissionNumber', sortable: true,
      searchValue: r => r.admissionNumber || '',
    },
    {
      label: 'Student Name', key: 'name',
      searchValue: r => `${r.firstName || ''} ${r.lastName || ''}`,
      render: r => `${r.firstName} ${r.lastName}`,
    },
    {
      label: 'Class', key: 'class',
      searchValue: r => getClassName(r.classId),
      render: r => getClassName(r.classId),
    },
    { label: 'Father', accessor: 'fatherName', searchValue: r => r.fatherName || '' },
    { label: 'Phone', accessor: 'fatherPhone', searchValue: r => r.fatherPhone || '' },
  ];

  const dropoutEntries = useMemo(() => {
    return dropouts.map(d => {
      const student = students.find(s => s.id === d.studentId) || {};
      return { ...d, student };
    });
  }, [dropouts, students]);

  const dropoutColumns = [
    {
      label: 'Admission No', key: 'admNo',
      searchValue: r => r.student?.admissionNumber || '',
      render: r => r.student?.admissionNumber || '-',
    },
    {
      label: 'Student Name', key: 'name',
      searchValue: r => `${r.student?.firstName || ''} ${r.student?.lastName || ''}`,
      render: r => r.student ? `${r.student.firstName} ${r.student.lastName}` : '-',
    },
    {
      label: 'Class', key: 'class',
      searchValue: r => getClassName(r.student?.classId),
      render: r => getClassName(r.student?.classId),
    },
    {
      label: 'Drop Out Date', accessor: 'dropoutDate', sortable: true,
      searchValue: r => r.dropoutDate || '',
      render: r => { if (!r.dropoutDate) return '-'; const d = new Date(r.dropoutDate); return `${String(d.getDate()).padStart(2,'0')}/${String(d.getMonth()+1).padStart(2,'0')}/${d.getFullYear()}`; },
    },
    { label: 'Academic Year', accessor: 'academicYear', sortable: true, searchValue: r => r.academicYear || '' },
    {
      label: 'Reason', accessor: 'reason',
      searchValue: r => r.reason || '',
      render: r => <span className="text-gray-600 text-xs">{r.reason || '-'}</span>,
    },
  ];

  return (
    <div>
      <div className="mb-6">
        <h1 className="text-2xl font-bold flex items-center gap-2">
          <UserX size={24} className="text-red-500" /> Drop Out
        </h1>
        <p className="text-gray-500 text-sm mt-0.5">Manage student drop outs and view history</p>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 mb-5 bg-gray-100 rounded-lg p-1 w-fit">
        <button
          onClick={() => setTab('active')}
          className={`px-4 py-1.5 rounded-md text-sm font-medium transition-colors ${tab === 'active' ? 'bg-white shadow text-gray-800' : 'text-gray-500 hover:text-gray-700'}`}>
          Active Students ({activeStudents.length})
        </button>
        <button
          onClick={() => setTab('dropouts')}
          className={`px-4 py-1.5 rounded-md text-sm font-medium transition-colors ${tab === 'dropouts' ? 'bg-white shadow text-gray-800' : 'text-gray-500 hover:text-gray-700'}`}>
          Dropped Out ({dropouts.length})
        </button>
      </div>

      {tab === 'active' && (
        <DataTable
          columns={activeColumns}
          data={activeStudents}
          actions={row => (
            <button
              onClick={() => setModalStudent(row)}
              className="flex items-center gap-1.5 px-3 py-1 bg-red-50 text-red-600 border border-red-200 rounded-lg text-xs hover:bg-red-100 font-medium">
              <UserX size={13} /> Mark Dropout
            </button>
          )}
        />
      )}

      {tab === 'dropouts' && (
        <DataTable
          columns={dropoutColumns}
          data={dropoutEntries}
          actions={row => (
            <button
              onClick={() => handleRestore(row)}
              className="flex items-center gap-1.5 px-3 py-1 bg-green-50 text-green-600 border border-green-200 rounded-lg text-xs hover:bg-green-100 font-medium">
              Restore
            </button>
          )}
        />
      )}

      {modalStudent && (
        <DropoutModal
          student={modalStudent}
          academicYear={academicYear}
          onClose={() => setModalStudent(null)}
          onSave={handleMarkDropout}
        />
      )}
    </div>
  );
}

export default Dropout;
